import {
  FormEvent, useState, useEffect, useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Stack, TextField } from '@fluentui/react';
import { Link } from 'react-router-dom';
import useDebounce from '../../components/hooks/useDebounce';
import { stackTokens15, textFieldStyles300 } from '../../utils';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import { toastError } from '../../utils/toast';
import PageHeader from '../../components/parts/PageHeader';
import BillitInvoiceOverview from '../billit-invoices/components/BillitInvoiceOverview';
import { BillitInvoice } from '../billit-invoices/types';

const BillitInvoicesSent = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [filter, setFilter] = useState('');
  const [invoices, setInvoices] = useState<BillitInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const debouncedValue = useDebounce(filter, 500);
  const limit = 50;
  // Enkel het antwoord van de laatste opvraging mag de lijst vullen.
  const requestSeq = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const fetchInvoices = async (
    searchFilter = '',
    currentOffset = 0,
    append = false,
  ) => {
    const isInitial = currentOffset === 0 && !append;
    // Een trager antwoord van een vorige zoekterm (bv. de lijst zonder filter bij
    // het openen) overschreef anders de zoekresultaten. Vorige opvraging annuleren
    // en verouderde antwoorden negeren.
    requestSeq.current += 1;
    const requestId = requestSeq.current;
    const isStale = () => requestId !== requestSeq.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const token = await getAccessTokenSilently();

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        filter: 'sent',
      });

      if (searchFilter) {
        params.append('search', searchFilter);
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_BACKEND_URI
        }/billit/invoices?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        },
      );

      if (isStale()) return;

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (isStale()) return;
      const newInvoices = data.invoices || [];
      const totalCount = data.total ?? 0;

      if (append) {
        setInvoices(prev => [...prev, ...newInvoices]);
      } else {
        setInvoices(newInvoices);
      }
      setTotal(totalCount);
      setOffset(currentOffset);
      setShowLoadMore(currentOffset + newInvoices.length < totalCount);
    } catch (error) {
      if (isStale() || (error instanceof DOMException && error.name === 'AbortError')) return;
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch invoices';
      dispatch(
        throwError({
          module: 'Billit Invoices Sent',
          message: errorMessage,
          level: SeverityLevel.Critical,
        }),
      );
      toastError(errorMessage);
    } finally {
      if (!isStale()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices(debouncedValue, 0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, isAuthenticated]);

  const loadMoreCallback = () => {
    if (loading || loadingMore) return;
    const nextOffset = offset + limit;
    if (nextOffset >= total) return;
    fetchInvoices(debouncedValue, nextOffset, true);
  };

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    setFilter(newValue || '');
  };

  const refetch = () => {
    fetchInvoices(debouncedValue, 0, false);
  };

  if (!isAuthenticated) return <p>Verboden</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title='Verstuurde Billit Facturen'
          iconName='Mail'
          loading={loading}
        />

        <div style={{ marginBottom: '12px' }}>
          <Link to='/billit-invoices' style={{ fontSize: '13px' }}>
            ← Terug naar Billit facturen
          </Link>
        </div>

        <TextField
          label='Zoek op factuurnummer of leverancier'
          placeholder='Factuurnummer of leverancier'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
        />
      </div>

      <BillitInvoiceOverview
        invoices={invoices}
        loading={loading}
        loadingMore={loadingMore}
        refetch={refetch}
        total={total}
        showLoadMore={showLoadMore}
        loadMoreCallback={loadMoreCallback}
      />
    </Stack>
  );
};

export default BillitInvoicesSent;
